import { authAPI } from "@/api/auth";
import { ToastMessage } from "@/components/toast-message";
import { auth } from "@/firebase";
import { toast } from "@/hooks/use-toast";
import { LOCALSTORAGE_KEY } from "@/utils/constants";
import { UserRole } from "@/utils/enum/user-role.enum";
import { AuthContextType, User } from "@/utils/interface/user";
import { onAuthStateChanged, signOut } from "firebase/auth";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const getTokenExpirationTime = (token: string): number | null => {
  try {
    const payload = token.split(".")[1];
    const decodedPayload = JSON.parse(atob(payload));
    return decodedPayload.exp * 1000; // Convert to milliseconds
  } catch (error) {
    console.error("Error parsing token:", error);
    return null;
  }
};

const isTokenExpired = (token: string): boolean => {
  if (!token) return true;
  const expirationTime = getTokenExpirationTime(token);
  if (!expirationTime) return true;

  // Add 5 minutes buffer before actual expiration
  const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds
  return Date.now() >= expirationTime - bufferTime;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<Partial<User> | null>(null);
  // const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  // const [setPasswordLoading, toggleSetPasswordLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem(LOCALSTORAGE_KEY.AUTH_TOKEN);

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // let currentToken = token;

          if (isTokenExpired(token || "")) {
            // Get new token from Firebase
            console.log("Token expired, refreshing...");
            const newFirebaseToken = await firebaseUser.getIdToken(true);
            localStorage.setItem(LOCALSTORAGE_KEY.AUTH_TOKEN, newFirebaseToken);
          }

          const user = await firebaseUser.getIdTokenResult();
          setUser({
            email: user.claims["email"] as string,
            role: user.claims["role"] as UserRole,
          });
        } catch (error) {
          await logout();
          setUser(null);
        }
      } else {
        setUser(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    setLoading(true);
    try {
      const result = await authAPI.login(email, password);
      setUser(result);
    } catch (error: any) {
      console.log("LOGIN ERROR --- ", error);
      if (error.code === "auth/invalid-credential") {
        toast({
          description: (
            <ToastMessage
              message="Looks like your email and password doesn't match."
              status="error"
            />
          ),
          duration: 3000,
        });
      } else if (error.code === "auth/user-not-found") {
        toast({
          description: (
            <ToastMessage
              message="No account found with this email."
              status="error"
            />
          ),
          duration: 3000,
        });
      } else if (error.code === "auth/wrong-password") {
        toast({
          description: (
            <ToastMessage message="Incorrect password." status="error" />
          ),
          duration: 3000,
        });
      } else if (error.code === "auth/too-many-requests") {
        toast({
          description: (
            <ToastMessage
              message="Too many failed attempts. Please try again later."
              status="error"
            />
          ),
          duration: 3000,
        });
      } else {
        toast({
          description: (
            <ToastMessage
              message="Login failed. Please try again."
              status="error"
            />
          ),
          duration: 3000,
        });
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    setLoading(true);
    try {
      await signOut(auth);
      setUser(null);
      localStorage.removeItem(LOCALSTORAGE_KEY.AUTH_TOKEN);
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setLoading(false);
    }
  };

  const setPassword = async (token: string, password: string) => {
    setLoading(true);
    try {
      const result = await authAPI.setPassword({ token, password });
      setUser(result);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    setPassword,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
